import { pickText, useTourState, useTourStoreApi } from "@heroncendre/player";
import { accueil } from "./choix.js";
import { POLICE_UI_SPLASH, boutonLangue, groupeLangues, separateurLangue, traitLangue } from "./splashStyles.js";

/**
 * ============================================================================
 *  SelecteurLangue.tsx — Le choix de la langue, au dessin de l'accueil
 * ----------------------------------------------------------------------------
 *  L'accueil est la référence de design : « FR | EN », capitales espacées, un
 *  trait sous la langue active. Ce composant est CE sélecteur, extrait pour que
 *  la visite l'emploie à son tour — à la place du panneau du moteur, honnête
 *  mais sans caractère.
 *
 *  Les langues viennent du document (`tour.languages`), le libellé accessible
 *  de `accueil.json` : rien en dur ici.
 *
 *  ── `surImage` ─────────────────────────────────────────────────────────────
 *
 *  Sur l'accueil, le sélecteur est posé sur un voile sombre. Dans la visite il
 *  est directement sur le panorama, qui peut être clair : une ombre portée
 *  légère garde les lettres lisibles sans ajouter de boîte, donc sans changer
 *  le dessin.
 * ==========================================================================*/

export function SelecteurLangue({ surImage = false }: { surImage?: boolean }) {
  const store = useTourStoreApi();
  const language = useTourState((state) => state.context.language);
  const langues = useTourState((state) => state.tour.languages);

  // Une visite à une seule langue n'a rien à proposer.
  if (langues.length < 2) return null;

  return (
    <div
      style={{
        ...groupeLangues,
        fontFamily: POLICE_UI_SPLASH,
        // La couche des contrôles ignore le pointeur (le canvas dessous doit le
        // recevoir) : chaque contrôle réclame le sien.
        pointerEvents: "auto",
        ...(surImage ? { textShadow: "0 0 3px rgba(0,0,0,.85), 0 1px 8px rgba(0,0,0,.7)" } : null),
      }}
    >
      <div role="group" aria-label={pickText(accueil.langue, language) ?? ""} style={groupeLangues}>
      {langues.map((code, index) => {
        const active = code === language;
        return (
          <span key={code} style={{ display: "flex", alignItems: "center" }}>
            {index > 0 && <span aria-hidden style={separateurLangue} />}
            <button
              type="button"
              aria-pressed={active}
              // À chaud : changer de langue ne relance rien.
              onClick={() => store.getState().setLanguage(code)}
              style={boutonLangue(active)}
            >
              {code.toUpperCase()}
              <span aria-hidden style={traitLangue(active)} />
            </button>
          </span>
        );
      })}
      </div>
    </div>
  );
}
